import { DataManGroupDef, GroupedData } from "../types/data";
import { DataManStateOptions } from "../types/state";

/**
 * Builds grouped data from flat data array based on groupBy configuration.
 * Groups items by the specified key and orders groups according to their order property.
 */
export const buildGroupedData = (
  data: any[],
  options: DataManStateOptions
): GroupedData => {
  const { groupByKey, groups = [], defaultGroup } = options;

  if (!groupByKey || !data?.length) {
    return [];
  }

  // Create a map of group definitions by id for quick lookup
  const groupMap = new Map<string, DataManGroupDef>();
  groups.forEach((group) => {
    groupMap.set(group.id, group);
  });

  // Group items by the groupByKey field value
  const groupedItems = new Map<string, any[]>();

  data.forEach((item) => {
    const groupId = item[groupByKey] ?? (defaultGroup?.id || "__default__");
    const existingItems = groupedItems.get(groupId) || [];
    existingItems.push(item);
    groupedItems.set(groupId, existingItems);
  });

  // Build the result array with group definitions and their items
  const result: GroupedData = [];

  // First, add groups in their defined order
  const sortedGroups = [...groups].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  sortedGroups.forEach((group) => {
    const items = groupedItems.get(group.id);
    if (items?.length) {
      result.push({
        group,
        items
      });
      groupedItems.delete(group.id);
    }
  });

  // Every item left matches no defined group: its value is empty, the default group's id
  // or one without a group definition
  if (defaultGroup) {
    const allDefaultItems = Array.from(groupedItems.values()).flat();

    if (allDefaultItems.length) {
      result.push({
        group: defaultGroup,
        items: allDefaultItems
      });
    }
  }

  return result;
};
